/* eslint-disable no-undef */
import { ChevronDown, ChevronRight } from "lucide-react"
import { useState } from "react"
import { NavLink as RouterLink, matchPath, useLocation } from "react-router-dom"
// material
import {
  Box,
  BoxProps,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText
} from "@mui/material"
import { alpha, styled, useTheme } from "@mui/material/styles"
import { useAuth } from "./auth/AuthProvider"

// ----------------------------------------------------------------------

interface ListItemStyleProps {
  component?: React.ElementType
  to?: string
}

const ListItemStyle = styled(ListItemButton)<ListItemStyleProps>(({ theme }) => ({
  ...theme.typography.body2,
  height: 48,
  position: "relative",
  textTransform: "capitalize",
  paddingLeft: theme.spacing(5),
  paddingRight: theme.spacing(2.5),
  color: theme.palette.text.secondary,
  "&:before": {
    top: 0,
    right: 0,
    width: 3,
    bottom: 0,
    content: "''",
    display: "none",
    position: "absolute",
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
    backgroundColor: theme.palette.primary.main
  }
}))

const ListItemIconStyle = styled(ListItemIcon)({
  width: 22,
  height: 22,
  display: "flex",
  alignItems: "center",
  justifyContent: "center"
})

// ----------------------------------------------------------------------

type NavItemProps = {
  title: string
  path: string
  target?: "_blank" | "_self" | "_parent" | "_top" | "framename"
  icon?: JSX.Element
  info?: JSX.Element
  corporateOnly?: boolean
  children?: {
    title: string
    path: string
  }[]
}

function NavItem({ item, isShow }: { item: NavItemProps; isShow?: boolean | undefined }) {
  const theme = useTheme()
  const { pathname } = useLocation()
  const { title, path, icon, info, children, target } = item
  const isActiveRoot = path ? !!matchPath({ path, end: false }, pathname) : false

  const [open, setOpen] = useState(isActiveRoot)

  const handleOpen = () => {
    setOpen(!open)
  }

  const activeRootStyle = {
    color: "primary.main",
    fontWeight: "fontWeightMedium",
    bgcolor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
    "&:before": { display: "block" }
  }

  const activeSubStyle = {
    color: "text.primary",
    fontWeight: "fontWeightMedium"
  }

  if (children) {
    return (
      <>
        <ListItemStyle
          onClick={handleOpen}
          sx={{
            ...(isActiveRoot && activeRootStyle)
          }}
        >
          <ListItemIconStyle>{icon && icon}</ListItemIconStyle>

          {isShow && (
            <>
              <ListItemText disableTypography primary={title} />
              {info && info}
              <Box sx={{ ml: 1, display: "inline-flex", alignItems: "center" }}>
                {open ? (
                  <ChevronDown size={16} aria-label="Collapse section" />
                ) : (
                  <ChevronRight size={16} aria-label="Expand section" />
                )}
              </Box>
            </>
          )}
        </ListItemStyle>

        {isShow && (
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {children.map((item) => {
                const { title, path } = item
                const isActiveSub = path ? !!matchPath({ path, end: false }, pathname) : false

                return (
                  <ListItemStyle
                    key={title}
                    component={RouterLink}
                    to={path}
                    sx={{
                      ...(isActiveSub && activeSubStyle)
                    }}
                  >
                    <ListItemIconStyle>
                      <Box
                        component="span"
                        sx={{
                          width: 4,
                          height: 4,
                          display: "flex",
                          borderRadius: "50%",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "text.disabled",
                          transition: (theme) => theme.transitions.create("transform"),
                          ...(isActiveSub && {
                            transform: "scale(2)",
                            bgcolor: "primary.main"
                          })
                        }}
                      />
                    </ListItemIconStyle>
                    <ListItemText disableTypography primary={title} />
                  </ListItemStyle>
                )
              })}
            </List>
          </Collapse>
        )}
      </>
    )
  }

  return (
    <ListItemStyle
      component={RouterLink}
      to={path}
      sx={{
        ...(isActiveRoot && activeRootStyle)
      }}
    >
      <ListItemIconStyle>{icon && icon}</ListItemIconStyle>
      {isShow && (
        <>
          <ListItemText disableTypography primary={title} />
          {info && info}
        </>
      )}
    </ListItemStyle>
  )
}

interface NavSectionProps extends BoxProps {
  isShow?: boolean | undefined
  navConfig: {
    subheader: string
    items: NavItemProps[]
  }[]
}

export default function NavSection({ navConfig, isShow = true, ...other }: NavSectionProps) {
  const auth = useAuth()
  const isCorporate = auth?.isCorporate ?? false

  return (
    <Box {...other}>
      {navConfig.map((list) => {
        const { subheader, items } = list

        // Filter out corporate-only items if user is not corporate
        const visibleItems = items.filter((item: NavItemProps) => {
          if (item.corporateOnly && !isCorporate) {
            return false
          }
          return true
        })

        // Don't render the section if there are no visible items
        if (visibleItems.length === 0) {
          return null
        }

        return (
          <List key={subheader} disablePadding>
            {visibleItems.map((item: NavItemProps) => (
              <NavItem key={item.title} item={item} isShow={isShow} />
            ))}
          </List>
        )
      })}
    </Box>
  )
}
